import type { ContactRow, UserContactOverlay } from './useContacts'
import { EmptyState } from '../../shared/ui'

/**
 * Read-render parity with the legacy contacts table at
 * crm/index.html:~2238. Same columns, same colored status dots, same
 * stage-class scheme. Row click delegates to legacy
 * `window.openContactDrawer(id)` so the drawer stays legacy this PR.
 */

interface Props {
  rows: ContactRow[]
  ucMap: Record<string, UserContactOverlay>
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  rotDays: number
  rotCutoff: string
  selectedIds: Set<string>
  onToggleRow: (id: string, checked: boolean) => void
  onToggleAll: (checked: boolean) => void
  visibleColumns: ColumnDef[]
  /** True when a search, filter or later page is narrowing the list. */
  isFiltered?: boolean
  onClearFilters?: () => void
}

type LegacyFn = (...args: unknown[]) => unknown
function callLegacy(name: 'openAddContact' | 'openImport'): void {
  const fn = (window as unknown as Record<string, unknown>)[name]
  if (typeof fn === 'function') (fn as LegacyFn)()
}

export interface ColumnDef {
  key: ContactColumnKey
  label: string
  visible: boolean
}
export type ContactColumnKey =
  | 'job_title'
  | 'primary_email'
  | 'primary_phone'
  | 'lifecycle_stage'
  | 'priority_score'
  | 'linkedin_url'
  | 'company_domain'
  | 'country'
  | 'industry'
  | 'last_contacted'

const NON_STALE_STAGES = new Set(['closed', 'do not contact'])

function stageClass(stage: string | null | undefined): string {
  if (!stage) return 's-new'
  const m: Record<string, string> = {
    cold: 's-cold',
    approaching: 's-approaching',
    active: 's-active',
    'meeting booked': 's-meeting',
    closed: 's-lost',
    won: 's-won',
    lost: 's-lost',
    prospect: 's-prospect',
    qualified: 's-qualified',
    demo: 's-demo',
    proposal: 's-proposal',
    negotiation: 's-negotiation',
    'closed won': 's-won',
    'closed lost': 's-lost',
    customer: 's-customer',
    new: 's-new',
  }
  return m[stage.toLowerCase()] ?? 's-new'
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function statusColor(s: string | null | undefined, kind: 'phone' | 'email'): string | null {
  if (!s) return null
  if (s === 'Good') return 'var(--green)'
  if (kind === 'phone' && s === 'Needs Number') return 'var(--amber)'
  if (kind === 'email' && s === 'Needs Email') return 'var(--amber)'
  return 'var(--red)'
}

export function ContactsTable({
  rows,
  ucMap,
  status,
  error,
  rotDays,
  rotCutoff,
  selectedIds,
  onToggleRow,
  onToggleAll,
  visibleColumns,
  isFiltered = false,
  onClearFilters,
}: Props) {
  const colCount = 2 + visibleColumns.length // checkbox + contact + variable

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r.id))

  // A brand-new account: nothing to filter yet, so show how to get people in.
  if (status === 'success' && rows.length === 0 && !isFiltered) {
    return (
      <div className="table-wrap">
        <EmptyState
          title="No contacts yet."
          message="Add the people you sell to one at a time, or bring them all in from a spreadsheet. Companies are created from each contact's company name."
          cta={{ label: '+ Add a contact', onClick: () => callLegacy('openAddContact') }}
          secondaryCta={{ label: 'Import CSV', onClick: () => callLegacy('openImport') }}
        />
      </div>
    )
  }

  return (
    <div className="table-wrap">
      <table>
        <thead id="c-thead">
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                className="row-check"
                ref={(el) => {
                  if (el) el.indeterminate = someSelected
                }}
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
                title="Select all"
              />
            </th>
            <th>Contact</th>
            {visibleColumns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody id="c-tbody">
          {status === 'loading' && (
            <tr className="loading-row">
              <td colSpan={colCount}>Loading…</td>
            </tr>
          )}
          {status === 'error' && (
            <tr className="loading-row">
              <td colSpan={colCount} style={{ color: 'var(--red)' }}>
                {error}
              </td>
            </tr>
          )}
          {status === 'success' && rows.length === 0 && (
            <tr className="empty-row">
              <td colSpan={colCount}>
                No contacts match your filters
                {onClearFilters && (
                  <div style={{ marginTop: 12 }}>
                    <button type="button" className="sec-btn" onClick={onClearFilters}>
                      Clear filters
                    </button>
                  </div>
                )}
              </td>
            </tr>
          )}
          {status === 'success' &&
            rows.map((c) => (
              <ContactRowView
                key={c.id}
                contact={c}
                uc={ucMap[c.id]}
                rotDays={rotDays}
                rotCutoff={rotCutoff}
                isSelected={selectedIds.has(c.id)}
                onToggle={(checked) => onToggleRow(c.id, checked)}
                visibleColumns={visibleColumns}
              />
            ))}
        </tbody>
      </table>
    </div>
  )
}

interface RowProps {
  contact: ContactRow
  uc: UserContactOverlay | undefined
  rotDays: number
  rotCutoff: string
  isSelected: boolean
  onToggle: (checked: boolean) => void
  visibleColumns: ColumnDef[]
}

function ContactRowView({
  contact: c,
  uc,
  rotDays,
  rotCutoff,
  isSelected,
  onToggle,
  visibleColumns,
}: RowProps) {
  const onRowClick = () => {
    const open = (window as unknown as { openContactDrawer?: (id: string) => void })
      .openContactDrawer
    open?.(c.id)
  }
  const stopBubble = (e: React.MouseEvent) => e.stopPropagation()

  const name = c.full_name?.trim() || [c.first_name, c.last_name].filter(Boolean).join(' ') || '—'
  const tags = uc?.tags ?? []

  return (
    <tr
      onClick={onRowClick}
      className={isSelected ? 'row-selected' : ''}
      style={{ cursor: 'pointer' }}
    >
      <td onClick={stopBubble}>
        <input
          type="checkbox"
          className="row-check"
          checked={isSelected}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </td>
      <td>
        <div className="company-cell">
          <div>
            <div className="contact-name">{name}</div>
            <div className="contact-company">{c.company_name ?? ''}</div>
            {tags.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 3 }}>
                {tags.map((t) => (
                  <span key={t} className="tag-pill-sm">
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </td>
      {visibleColumns.map((col) => (
        <Cell
          key={col.key}
          colKey={col.key}
          c={c}
          uc={uc}
          rotDays={rotDays}
          rotCutoff={rotCutoff}
        />
      ))}
    </tr>
  )
}

interface CellProps {
  colKey: ContactColumnKey
  c: ContactRow
  uc: UserContactOverlay | undefined
  rotDays: number
  rotCutoff: string
}

function Cell({ colKey, c, uc, rotDays, rotCutoff }: CellProps) {
  switch (colKey) {
    case 'job_title':
      return <td className="td-mono">{c.job_title ?? '—'}</td>
    case 'primary_email': {
      const color = statusColor(uc?.email_status, 'email')
      return (
        <td className="td-mono" style={{ whiteSpace: 'nowrap' }}>
          {c.primary_email ?? '—'}
          {color ? (
            <span
              title={uc?.email_status ?? ''}
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                marginLeft: 5,
                verticalAlign: 'middle',
                background: color,
              }}
            />
          ) : null}
        </td>
      )
    }
    case 'primary_phone': {
      const color = statusColor(uc?.phone_status, 'phone')
      return (
        <td className="td-mono" style={{ whiteSpace: 'nowrap' }}>
          {c.primary_phone ?? '—'}
          {color ? (
            <span
              title={uc?.phone_status ?? ''}
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                marginLeft: 5,
                verticalAlign: 'middle',
                background: color,
              }}
            />
          ) : null}
        </td>
      )
    }
    case 'lifecycle_stage': {
      const stage = uc?.lifecycle_stage
      const stageLow = (stage ?? '').toLowerCase()
      const isStale =
        stage &&
        !NON_STALE_STAGES.has(stageLow) &&
        (!uc?.last_contacted || uc.last_contacted < rotCutoff)
      return (
        <td>
          <span className={`stage-badge ${stageClass(stage)}`}>{stage ?? 'New'}</span>
          {isStale ? (
            <span className="stale-dot" title={`No contact in ${rotDays}+ days`}>
              ●
            </span>
          ) : null}
        </td>
      )
    }
    case 'priority_score': {
      const sc = uc?.priority_score
      const klass =
        sc != null && sc >= 70 ? 'score-high' : sc != null && sc >= 40 ? 'score-mid' : 'score-low'
      return (
        <td>
          <span className={`score-pill ${klass}`}>{sc != null ? sc : '—'}</span>
        </td>
      )
    }
    case 'linkedin_url':
      return (
        <td>
          {c.linkedin_url ? (
            <a
              className="li-link"
              href={c.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              ↗
            </a>
          ) : (
            '—'
          )}
        </td>
      )
    case 'company_domain':
      return <td className="td-mono">{c.company_domain ?? '—'}</td>
    case 'country':
      return <td className="td-mono">{c.country ?? '—'}</td>
    case 'industry':
      return <td className="td-mono">{c.industry ?? '—'}</td>
    case 'last_contacted':
      return <td className="td-mono">{fmtDate(uc?.last_contacted)}</td>
    default:
      return <td>—</td>
  }
}
